import React from "react";
import QmsVaultStaffAvatar from "./QmsVaultStaffAvatar";
import {
  formatVaultTableDate,
  getVaultPersonDisplayStatus,
  getVaultPersonProgressLabel,
  getVaultPersonSentDate,
  hasVaultPersonSelfCompleted,
  isVaultPersonSent,
} from "./qmsVaultWorkspaceUtils";

function getStatusTone(status, isSent) {
  if (!isSent) return "assigned";
  const value = String(status || "").trim().toLowerCase();
  if (!value || value === "assigned") return "assigned";
  if (value === "sent") return "warning";
  if (value.includes("complete") || value.includes("approved")) return "success";
  if (value.includes("reject") || value.includes("declin")) return "danger";
  if (value.includes("pending")) return "warning";
  return "info";
}

export function QmsVaultPersonStatusBadge({ row, config }) {
  const isSent = isVaultPersonSent(row, config.statusField, config.sentDateField);
  const label = getVaultPersonDisplayStatus(
    row,
    config.statusField,
    config.sentDateField,
  );
  const tone = getStatusTone(label, isSent);
  return <span className={`qms-vault-people__status is-${tone}`}>{label}</span>;
}

export function QmsVaultPersonCard({
  row,
  config,
  readOnly = false,
  canSave = false,
  canRemove = false,
  isBusy = false,
  isRemoving = false,
  onRemoveRequest,
}) {
  const id = String(row[config.savedIdField] || "");
  const name = row[config.savedNameField] || "—";
  const isSent = isVaultPersonSent(row, config.statusField, config.sentDateField);
  const sentDate = getVaultPersonSentDate(row, config.sentDateField, config.statusField);
  const commentText = String(row[config.commentField] ?? "").trim();
  const commentDateField = config.commentDateField || config.actionDateField;
  const commentDate = commentDateField
    ? formatVaultTableDate(row[commentDateField])
    : "—";
  const commentPreviewLimit = Number(config.commentPreviewLimit) || 120;
  const isLongComment = commentText.length > commentPreviewLimit;
  const showActionDate = config.showActionDate !== false;
  const actionDate = isSent ? formatVaultTableDate(row[config.actionDateField]) : "—";
  const statusLabel = getVaultPersonDisplayStatus(
    row,
    config.statusField,
    config.sentDateField,
  );
  const isCompletedReviewer = hasVaultPersonSelfCompleted(row, config)
    || /approved/i.test(statusLabel);
  const removable = canRemove && canSave && !readOnly;

  return (
    <article
      className={`qms-vault-audit__card qms-vault-people__card${
        isRemoving ? " is-removing" : ""
      }${isCompletedReviewer ? " is-completed-reviewer" : ""}`}
    >
      <div className="qms-vault-audit__card-head">
        <div className="qms-vault-audit__card-title-wrap">
          {config.icon && !config.hideIcon ? (
            <span className="qms-vault-people__card-icon" aria-hidden="true">
              <i className={config.icon} />
            </span>
          ) : null}
          <div className="qms-vault-people__card-name-wrap">
            <QmsVaultStaffAvatar name={name} compact title={name} />
            <h6 className="qms-vault-audit__card-title">{name}</h6>
          </div>
        </div>
        <QmsVaultPersonStatusBadge row={row} config={config} />
      </div>

      <div className="qms-vault-audit__card-meta">
        <span className="qms-vault-audit__card-meta-item">
          <i className="far fa-paper-plane" aria-hidden="true" />
          {config.sentLabel}: {sentDate}
        </span>
        {showActionDate ? (
          <span className="qms-vault-audit__card-meta-item">
            <i className="far fa-calendar-check" aria-hidden="true" />
            {config.actionLabel}: {actionDate}
          </span>
        ) : null}
        <span className="qms-vault-audit__card-meta-item">
          <i className="far fa-flag" aria-hidden="true" />
          {getVaultPersonProgressLabel(row, config)}
        </span>
      </div>

      {commentText ? (
        <div
          className={`qms-vault-audit__card-notes${isLongComment ? " has-preview" : ""}`}
          title={isLongComment ? commentText : undefined}
        >
          <div className="qms-vault-audit__card-notes-head">
            <span className="qms-vault-audit__card-notes-label">
              {config.commentLabel || "Comment"}
            </span>
            {commentDate && commentDate !== "—" ? (
              <span className="qms-vault-audit__card-notes-datetime">{commentDate}</span>
            ) : null}
          </div>
          <p
            className={`qms-vault-audit__card-notes-text${isLongComment ? " is-clamped" : ""}`}
          >
            {commentText}
          </p>
          {isLongComment ? (
            <div className="qms-vault-audit__card-notes-preview" role="tooltip">
              {commentText}
            </div>
          ) : null}
        </div>
      ) : null}

      {removable ? (
        <div className="qms-vault-audit__card-footer">
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-danger qms-vault-people__remove-link"
            disabled={isBusy}
            onClick={() => onRemoveRequest?.(id)}
          >
            {isRemoving ? (
              <>
                <i className="fas fa-spinner fa-spin me-1" aria-hidden="true" />
                Removing…
              </>
            ) : (
              <>
                <i className="fas fa-trash-alt me-1" aria-hidden="true" />
                Remove
              </>
            )}
          </button>
        </div>
      ) : null}
    </article>
  );
}

export const REVIEWER_CARD_CONFIG = {
  savedIdField: "ReviewerId",
  savedNameField: "ReviewerName",
  sentDateField: "ReviewSentDate",
  commentField: "ReviewerComment",
  commentDateField: "ReviewedDate",
  commentLabel: "Last reviewer comment",
  actionDateField: "ReviewedDate",
  statusField: "ReviewStatus",
  completedAtField: "ReveiwCompletedDateTime",
  showActionDate: false,
  hideIcon: true,
  sentLabel: "Sent for review",
  actionLabel: "Reviewed on",
  completedLabel: "Review completed",
  pendingCompletedLabel: "In review",
  actionedLabel: "Reviewed",
  icon: "fas fa-user-check",
};

export const APPROVER_CARD_CONFIG = {
  savedIdField: "ApproverId",
  savedNameField: "ApproverName",
  sentDateField: "ApproveSentDate",
  commentField: "ApproverComment",
  commentDateField: "ApprovedDate",
  commentLabel: "Last approver comment",
  actionDateField: "ApprovedDate",
  statusField: "ApproveStatus",
  completedAtField: "ApprovalCompletedDateTime",
  showActionDate: false,
  hideIcon: true,
  sentLabel: "Sent for approval",
  actionLabel: "Approved on",
  completedLabel: "Approval completed",
  pendingCompletedLabel: "In approval",
  actionedLabel: "Responded",
  icon: "fas fa-user-shield",
};
